import { sendAuditEvent } from '@/lib/auditBeacon';

/**
 * Report what the user does inside a page that no server request records:
 * a button / menu item / option / checkbox clicked (CLICK), a tab switched (TAB),
 * a dialog opened (MODAL_OPEN). One listener on the document covers the shell and
 * every MFE, since they all render into it.
 *
 * Only the element's visible label is sent — never what was typed into a field.
 * Labels are clipped and long digit runs masked. Mark an element (or a whole area)
 * with `data-audit-ignore` to keep it out, or give it `data-audit-label` to name it.
 */
type AuditInteractionAction = 'CLICK' | 'TAB' | 'MODAL_OPEN';

const CLICKABLE = [
  'button',
  '[role="button"]',
  '[role="tab"]',
  '[role="menuitem"]',
  '[role="menuitemcheckbox"]',
  '[role="menuitemradio"]',
  '[role="option"]',
  '[role="checkbox"]',
  '[role="switch"]',
  '[role="radio"]',
  'input[type="checkbox"]',
  'input[type="radio"]',
  'input[type="button"]',
  'input[type="submit"]',
].join(',');
const DIALOG = '[role="dialog"],[role="alertdialog"]';
const IGNORE = '[data-audit-ignore]';
const LABEL_MAX = 80;
/** The same action on the same label inside this window is one event (double clicks, re-renders). */
const REPEAT_WINDOW_MS = 1500;

let stopCurrent: (() => void) | null = null;
let last: { key: string; at: number } | null = null;

/** Visible label, whitespace collapsed, digit runs (phone, account numbers) masked, clipped. */
function cleanLabel(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const text = raw.replace(/\s+/g, ' ').replace(/\d{6,}/g, '••••••').trim();
  if (!text) return null;
  return text.length > LABEL_MAX ? `${text.slice(0, LABEL_MAX - 1)}…` : text;
}

function textOfIds(ids: string | null): string | null {
  if (!ids) return null;
  const text = ids
    .split(/\s+/)
    .map((id) => document.getElementById(id)?.textContent ?? '')
    .join(' ');
  return cleanLabel(text);
}

function labelOf(el: HTMLElement): string | null {
  const explicit = el.getAttribute('data-audit-label') || el.getAttribute('aria-label');
  if (explicit) return cleanLabel(explicit);
  const byId = textOfIds(el.getAttribute('aria-labelledby'));
  if (byId) return byId;
  if (el instanceof HTMLInputElement) {
    // Button-type inputs show their value as the caption; checkboxes/radios use their <label>.
    if (el.type === 'button' || el.type === 'submit') return cleanLabel(el.value);
    return cleanLabel(el.labels?.[0]?.textContent ?? el.title);
  }
  return cleanLabel(el.textContent) ?? cleanLabel(el.title);
}

function dialogLabel(el: HTMLElement): string | null {
  return (
    cleanLabel(el.getAttribute('data-audit-label') || el.getAttribute('aria-label')) ??
    textOfIds(el.getAttribute('aria-labelledby')) ??
    cleanLabel(el.querySelector('h1, h2, h3, [data-dialog-title]')?.textContent)
  );
}

function report(action: AuditInteractionAction, label: string): void {
  const key = `${action}|${label}`;
  const now = Date.now();
  if (last && last.key === key && now - last.at < REPEAT_WINDOW_MS) return;
  last = { key, at: now };
  sendAuditEvent({ action, resource: 'ui', resourceLabel: label, page: window.location.pathname.slice(0, 200) });
}

function onClick(event: MouseEvent): void {
  try {
    const target = event.target instanceof Element ? event.target : null;
    const el = target?.closest<HTMLElement>(CLICKABLE);
    if (!el || el.closest(IGNORE)) return;
    if (el.matches(':disabled') || el.getAttribute('aria-disabled') === 'true') return;
    const label = labelOf(el);
    if (!label) return; // icon-only control with no accessible name: nothing worth showing
    report(el.getAttribute('role') === 'tab' ? 'TAB' : 'CLICK', label);
  } catch {
    /* the log must never break a click */
  }
}

function dialogsIn(node: Node): HTMLElement[] {
  if (!(node instanceof HTMLElement)) return [];
  const found = node.matches(DIALOG) ? [node] : [];
  return found.concat(Array.from(node.querySelectorAll<HTMLElement>(DIALOG)));
}

/** Start reporting in-page interactions; repeat calls are no-ops. Returns a stop function. */
export function startInteractionAudit(): () => void {
  if (stopCurrent) return stopCurrent;
  try {
    const seen = new WeakSet<HTMLElement>();
    const observer = new MutationObserver((mutations) => {
      try {
        for (const m of mutations) {
          for (const node of Array.from(m.addedNodes)) {
            for (const dialog of dialogsIn(node)) {
              if (seen.has(dialog) || dialog.closest(IGNORE)) continue;
              seen.add(dialog);
              const label = dialogLabel(dialog);
              if (label) report('MODAL_OPEN', label);
            }
          }
        }
      } catch {
        /* the log must never break rendering */
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
    document.addEventListener('click', onClick, true);
    stopCurrent = () => {
      observer.disconnect();
      document.removeEventListener('click', onClick, true);
      stopCurrent = null;
    };
    return stopCurrent;
  } catch {
    return () => undefined;
  }
}
